import { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";

import { getFlattenSelectedCategory } from "app/pages/industry-analysis/common/getSelectedCategory";

import { GROWTH_OVERVIEW_CATEGORY_PAGE_SIZE } from "../const";
import type { GrowthOverviewResponse } from "../growthOverviewApiTypes";
import type { GrowthOverviewLevel } from "../types";
import type { IGrowthOverviewTableRow } from "../types";
import { clampPaginatedGrowthOverviewResponse } from "../utils/clampPaginatedGrowthOverviewResponse";
import type { IGrowthOverviewPaginationParams } from "../utils/clampPaginatedGrowthOverviewResponse";
import { appendRowsWithDedup, mergeRefreshedRootRows } from "../utils/growthOverviewPaginationUtils";
import { mapApiResponseToTableRows, mapBranchResponseToTableRows } from "../utils/mapApiRows";
import { mergeBranchIntoTree } from "../utils/mergeBranchIntoTree";
import { buildGrowthOverviewRootPaginationParams } from "../utils/resolveGrowthOverviewCategoryLevel";

import { useGrowthOverviewBranchData } from "./useGrowthOverviewBranchData";
import { useGrowthOverviewData } from "./useGrowthOverviewData";

interface ILoadGrowthOverviewBranchOptions {
    domainOffset?: number;
    subIndustryOffset?: number;
}

const mapRootQueryToRows = (
    data: NonNullable<ReturnType<typeof useGrowthOverviewData>["data"]>,
    rootPaginationParams: ReturnType<typeof buildGrowthOverviewRootPaginationParams>,
): IGrowthOverviewTableRow[] =>
    mapApiResponseToTableRows(clampPaginatedGrowthOverviewResponse(data, rootPaginationParams));

export const useGrowthOverviewLazyTree = (
    granularity: Parameters<typeof useGrowthOverviewData>[0],
    metric: Parameters<typeof useGrowthOverviewData>[1],
    debouncedSearchTerm: Parameters<typeof useGrowthOverviewData>[2],
) => {
    const { params } = useCurrentStateAndParams();
    const { category } = params;
    const rootQuery = useGrowthOverviewData(granularity, metric, debouncedSearchTerm);
    const { fetchBranch, fetchIndustriesPage, fetchSubIndustriesPage } = useGrowthOverviewBranchData();
    const paginationResetKey = useMemo(() => JSON.stringify(rootQuery.queryKey), [rootQuery.queryKey]);
    const mappedRootRows = useMemo(() => {
        if (!rootQuery.data) {
            return [];
        }

        return mapRootQueryToRows(
            rootQuery.data,
            buildGrowthOverviewRootPaginationParams(getFlattenSelectedCategory()),
        );
    }, [rootQuery.data]);
    const prevPaginationResetKeyRef = useRef(paginationResetKey);

    const [rows, setRows] = useState<IGrowthOverviewTableRow[]>([]);
    const [loadingRowIds, setLoadingRowIds] = useState<ReadonlySet<string>>(() => new Set());
    const [isLoadingMoreIndustries, setIsLoadingMoreIndustries] = useState(false);
    const [isLoadingMoreSubIndustries, setIsLoadingMoreSubIndustries] = useState(false);

    useLayoutEffect(() => {
        const resetKeyChanged = prevPaginationResetKeyRef.current !== paginationResetKey;
        if (resetKeyChanged) {
            prevPaginationResetKeyRef.current = paginationResetKey;
        }

        if (!rootQuery.data) {
            if (resetKeyChanged) {
                setRows([]);
                setLoadingRowIds(new Set());
            }
            return;
        }

        if (resetKeyChanged) {
            setRows(mappedRootRows);
            setLoadingRowIds(new Set());
            return;
        }

        setRows((current) => mergeRefreshedRootRows(current, mappedRootRows));
    }, [mappedRootRows, paginationResetKey, rootQuery.data]);

    const isAwaitingRootData =
        ((rootQuery.isLoading || rootQuery.isFetching) && rootQuery.data === undefined) ||
        (rootQuery.data !== undefined && rows.length === 0 && mappedRootRows.length > 0);

    const loadBranch = useCallback(
        async (row: IGrowthOverviewTableRow, options: ILoadGrowthOverviewBranchOptions = {}) => {
            const domainOffset = options.domainOffset ?? 0;
            const subIndustryOffset = options.subIndustryOffset ?? 0;
            const isAppend = domainOffset > 0 || subIndustryOffset > 0;
            if (!isAppend && row.childrenLoaded) {
                return;
            }

            if (loadingRowIds.has(row.id)) {
                return;
            }

            setLoadingRowIds((current) => new Set(current).add(row.id));

            try {
                const response = await fetchBranch(row.category, row.rowType, { domainOffset, subIndustryOffset });
                setRows((current) => mergeBranchIntoTree(current, row.id, response, { append: isAppend }));
            } finally {
                setLoadingRowIds((current) => {
                    const next = new Set(current);
                    next.delete(row.id);
                    return next;
                });
            }
        },
        [fetchBranch, loadingRowIds],
    );

    const loadMoreTopLevelRows = useCallback(
        async (
            fetchPage: (offset: number, limit: number) => Promise<GrowthOverviewResponse>,
            buildClampParams: (offset: number) => IGrowthOverviewPaginationParams,
            isLoading: boolean,
            setIsLoading: (loading: boolean) => void,
        ) => {
            if (isLoading) {
                return;
            }

            const totalCount = rootQuery.data?.totalCount ?? 0;
            const offset = rows.length;
            if (offset >= totalCount) {
                return;
            }

            setIsLoading(true);

            try {
                const response = await fetchPage(offset, GROWTH_OVERVIEW_CATEGORY_PAGE_SIZE);
                const nextRows = mapBranchResponseToTableRows(
                    clampPaginatedGrowthOverviewResponse(response, buildClampParams(offset)),
                );
                if (nextRows.length === 0) {
                    return;
                }

                setRows((current) => appendRowsWithDedup(current, nextRows));
            } finally {
                setIsLoading(false);
            }
        },
        [rootQuery.data?.totalCount, rows.length],
    );

    const loadMoreIndustries = useCallback(
        () =>
            loadMoreTopLevelRows(
                fetchIndustriesPage,
                (industryOffset) => ({
                    industryOffset,
                    industryLimit: GROWTH_OVERVIEW_CATEGORY_PAGE_SIZE,
                }),
                isLoadingMoreIndustries,
                setIsLoadingMoreIndustries,
            ),
        [fetchIndustriesPage, isLoadingMoreIndustries, loadMoreTopLevelRows],
    );

    const loadMoreSubIndustries = useCallback(
        () =>
            loadMoreTopLevelRows(
                fetchSubIndustriesPage,
                (subIndustryOffset) => ({
                    subIndustryOffset,
                    subIndustryLimit: GROWTH_OVERVIEW_CATEGORY_PAGE_SIZE,
                }),
                isLoadingMoreSubIndustries,
                setIsLoadingMoreSubIndustries,
            ),
        [fetchSubIndustriesPage, isLoadingMoreSubIndustries, loadMoreTopLevelRows],
    );

    return {
        rows,
        responseLevel: rootQuery.data?.level as GrowthOverviewLevel | undefined,
        totalCount: rootQuery.data?.totalCount,
        isLoading: rootQuery.isLoading,
        isError: rootQuery.isError,
        isFetching: rootQuery.isFetching,
        isAwaitingRootData,
        loadingRowIds,
        isLoadingMoreIndustries,
        isLoadingMoreSubIndustries,
        loadBranch,
        loadMoreIndustries,
        loadMoreSubIndustries,
        rootCategory: category,
    };
};
